/**
 * Shared view-state for the world-entity CRUD grids (NPCs / Locations /
 * Factions). Each dumb view used to hand-roll the same block: a filter
 * segment, a selected-id + editing flag, a create-modal flag, and the derived
 * pending/accepted counts + the "pending first, then alphabetical" visible
 * list. This hook owns that identical machinery so a view keeps only its
 * entity-specific state (Locations' tree toggle, Factions' relationship
 * draft) and its two Mantine forms.
 *
 * It is presentation-only — no data fetching, no API — so it stays inside the
 * dumb half and the smart wrapper never sees it.
 */

import { useCallback, useEffect, useMemo, useState } from "react"

export type TWorldEntityFilter = "all" | "pending" | "accepted"

interface IUseWorldEntityGridArgs<TItem> {
  /** Full entity list from the smart wrapper (already fetched). */
  items: TItem[]
  /** Whether an item is a pending (world-extraction proposed) entry. */
  isPending: (item: TItem) => boolean
  /** Display name — drives the alphabetical tiebreak in the visible-list sort. */
  getName: (item: TItem) => string
  /** Stable id — drives selection lookup. */
  getId: (item: TItem) => string
  /** Deep-link target: auto-selects this id on mount / when it changes. */
  initialEntityId?: string
}

interface IUseWorldEntityGridResult<TItem> {
  filter: TWorldEntityFilter
  setFilter: (filter: TWorldEntityFilter) => void
  selectedId: string | null
  selected: TItem | null
  editing: boolean
  setEditing: (editing: boolean) => void
  createModalOpen: boolean
  setCreateModalOpen: (open: boolean) => void
  pendingCount: number
  acceptedCount: number
  /** Filtered + sorted (pending first, then alphabetical) list for the grid. */
  visibleItems: TItem[]
  /** Every pending item, unfiltered — for bulk-action wiring. */
  pendingItems: TItem[]
  /** Open the detail drawer for an id (leaves edit mode). */
  selectDetail: (id: string) => void
  /** Close the drawer (clears selection + edit mode). */
  closeDrawer: () => void
}

export function useWorldEntityGrid<TItem>({
  items,
  isPending,
  getName,
  getId,
  initialEntityId,
}: IUseWorldEntityGridArgs<TItem>): IUseWorldEntityGridResult<TItem> {
  const [filter, setFilter] = useState<TWorldEntityFilter>("all")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editing, setEditing] = useState<boolean>(false)
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false)

  const pendingCount = useMemo(() => items.filter(isPending).length, [items, isPending])
  const acceptedCount = items.length - pendingCount

  const visibleItems = useMemo<TItem[]>(() => {
    let list = items
    if (filter === "pending") list = list.filter(isPending)
    else if (filter === "accepted") list = list.filter((item) => !isPending(item))
    return [...list].sort((a, b) => {
      const aPending = isPending(a)
      const bPending = isPending(b)
      if (aPending && !bPending) return -1
      if (!aPending && bPending) return 1
      return getName(a).localeCompare(getName(b))
    })
  }, [items, filter, isPending, getName])

  const selected = useMemo(() => items.find((item) => getId(item) === selectedId) ?? null, [items, selectedId, getId])

  const pendingItems = useMemo(() => items.filter(isPending), [items, isPending])

  const selectDetail = useCallback((id: string): void => {
    setSelectedId(id)
    setEditing(false)
  }, [])

  const closeDrawer = useCallback((): void => {
    setSelectedId(null)
    setEditing(false)
  }, [])

  // Mirror an inbound deep-link id (e.g. a chronicle chip click) into the
  // drawer state. We only react when the value changes — we don't fight the
  // user closing the drawer manually.
  useEffect(() => {
    if (initialEntityId) {
      setSelectedId(initialEntityId)
      setEditing(false)
    }
  }, [initialEntityId])

  return {
    filter,
    setFilter,
    selectedId,
    selected,
    editing,
    setEditing,
    createModalOpen,
    setCreateModalOpen,
    pendingCount,
    acceptedCount,
    visibleItems,
    pendingItems,
    selectDetail,
    closeDrawer,
  }
}
